import { IPType } from './enums';
import { Validator } from 'ip-num';
import { isEmpty } from 'lodash';

const IP_REGEX = String.raw`^((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])(\.(?!$)|$)){4}$`;
const PRIVATE_IP_REGEX = String.raw`(^127\.0\.0\.1)|(^192\.168)|(^192\.0\.0)|(^10\.)|(^172\.1[6-9])|(^172\.2\d)|(^172\.3[0-1])`;
const BROADCAST_IP1 = '0.0.0.0'; // NOSONAR - intentional broadcast address boundary, not a server address
const BROADCAST_IP2 = '255.255.255.255'; // NOSONAR - intentional broadcast address boundary, not a server address
const REGEX_RECOGNIZE_IPV6 = /[abcdef:]/g;
export const INVALID_IP_RANGE = 'Invalid IP Range';
export const INVALID_PRIVATE_IP = 'Private IP address isn\'t allowed';
export const INVALID_IP_MESSAGE = 'Invalid IP Address';
// RFC 1918 private IP ranges used for validation — hardcoded values are intentional boundaries, not server addresses. // NOSONAR
const privateIpRanges: { lowerBoundary: string, upperBoundary: string }[] = [
    {
        lowerBoundary: '10.0.0.0',       // NOSONAR
        upperBoundary: '10.255.255.255'  // NOSONAR
    }, {
        lowerBoundary: '127.0.0.1',      // NOSONAR
        upperBoundary: '127.0.0.1'       // NOSONAR
    }, {
        lowerBoundary: '172.16.0.0',     // NOSONAR
        upperBoundary: '172.31.255.255'  // NOSONAR
    }, {
        lowerBoundary: '192.168.0.0',    // NOSONAR
        upperBoundary: '192.168.255.255' // NOSONAR
    }, {
        lowerBoundary: '192.0.0.0',      // NOSONAR
        upperBoundary: '192.0.0.255'     // NOSONAR
    }
];

const isIpV6Type = (ip: IPType) => ip === IPType.IPV6;

const ipAddressToInt = (ipAddress: string) => {
    const octets = ipAddress.split('.').map(Number);
    const intIp = (
        (octets[0] << 24) +
        (octets[1] << 16) +
        (octets[2] << 8) +
        octets[3]
    );
    return intIp >>> 0;
};

const isRangeMatchingPrivateIpsPattern = (lowIP: string, highIP: string) =>  {
    return privateIpRanges.some(range =>
        isPrivateAddressInRange(ipAddressToInt(lowIP), ipAddressToInt(highIP), range)
    );
};

const isPrivateAddressInRange = (receivedRangeLower: number, receivedRangeUpper: number, range: {lowerBoundary: string, upperBoundary: string }) =>  {
    const privateRangeLower = ipAddressToInt(range.lowerBoundary);
    const privateRangeUpper = ipAddressToInt(range.upperBoundary);

    return (privateRangeLower <= receivedRangeLower && privateRangeUpper >= receivedRangeLower) ||
    (privateRangeLower <= receivedRangeUpper && privateRangeUpper >= receivedRangeLower) ||
    (receivedRangeLower <= privateRangeLower && receivedRangeUpper >= privateRangeLower);
};

const isBroadcast = (ip: string): boolean => {
    return ip === BROADCAST_IP1 || ip === BROADCAST_IP2;
};

const isValidIPv4Address = (ip: string): string => {
    return ip === '' || new RegExp(IP_REGEX).test(ip) ? '' : INVALID_IP_MESSAGE;
};

export const isPrivateIPv4Range = (lowIP: string, highIP: string) =>  {
    return isRangeMatchingPrivateIpsPattern(lowIP, highIP);
};

export const isNotPrivateIPv4 = (ip: string): string => {
    return new RegExp(PRIVATE_IP_REGEX).test(ip) || isBroadcast(ip) ? INVALID_PRIVATE_IP : '';
};

export const isValidIPv4 = (ip: string): string => {
    return isValidIPv4Address(ip) || isNotPrivateIPv4(ip);
};

export const isValidIPv6 = (ip: string): string => {
    return ip === '' || (Validator.isValidIPv6String(ip)[0]) ? '' : INVALID_IP_MESSAGE;
};

export const isValidIPInRange = (ipRange: string, ipType: IPType, isIPV6supported = false): string => {
    const ips = ipRange.split('-');
    const [lowIP, highIP] = ips;
    const shouldValidateIpv6Type = isIPV6supported && isIpV6Type(ipType);

    const errorLow = shouldValidateIpv6Type ? isValidIPv6(lowIP) : isValidIPv4(lowIP) === INVALID_IP_MESSAGE;
    const errorHigh = shouldValidateIpv6Type ? isValidIPv6(highIP) : isValidIPv4(highIP) === INVALID_IP_MESSAGE;
    if (errorLow || errorHigh)
        return INVALID_IP_MESSAGE;
    return isValidIPRange(ipRange, isIPV6supported);
};

export const isValidSubnetIP = (ipSubnet: string, ipType: IPType, isIPV6supported = false): string => {
    const ips = ipSubnet.split('/');
    const [lowIP, subnet] = ips;
    const shouldValidateIpv6Type = isIPV6supported && isIpV6Type(ipType);

    const errorIp = isEmpty(lowIP) || shouldValidateIpv6Type ? isValidIPv6(lowIP) : (isValidIPv4(lowIP));
    const errorSubnet = isEmpty(subnet);
    const errorAddress = shouldValidateIpv6Type ? Validator.isValidIPv6CidrRange(ipSubnet) :
        Validator.isValidIPv4CidrRange(ipSubnet);
    if (isNotPrivateIPv4(lowIP))
        return INVALID_PRIVATE_IP;
    if (errorIp || errorSubnet || !errorAddress[0])
        return INVALID_IP_MESSAGE;
    return '';
};

export const isValidIPRange = (ipRange: string, isIPV6supported = false): string => {
    if (!ipRange.includes('-')) return '';

    const ips = ipRange.split('-');
    if (ips.length !== 2) return INVALID_IP_RANGE;
    const [lowIP, highIP] = ips;
    if (lowIP === highIP) return INVALID_IP_RANGE;
    if (!lowIP || !highIP) return INVALID_IP_RANGE;

    const ipType = new RegExp(REGEX_RECOGNIZE_IPV6).test(ips[0]) ? IPType.IPV6 : IPType.IPV4;
    let numOfOctets, lowIPOctets, highIPOctets;
    if (isIPV6supported && isIpV6Type(ipType)) {
        numOfOctets = 8;
        lowIPOctets = lowIP.split(':').map(hexString => Number.parseInt(hexString, 16));
        highIPOctets = highIP.split(':').map(hexString => Number.parseInt(hexString, 16));
    } else {
        numOfOctets = 4;
        if(isPrivateIPv4Range(lowIP, highIP))  return INVALID_PRIVATE_IP;
        lowIPOctets = lowIP.split('.').map(Number);
        highIPOctets = highIP.split('.').map(Number);
    }

    for (let i = 0; i < numOfOctets; i++) {
        if (lowIPOctets[i] > highIPOctets[i]) {
            return INVALID_IP_RANGE;
        } else if (lowIPOctets[i] < highIPOctets[i]) {
            break;
        }
    }

    return '';
};

export const checkIP = (value: string, isIPV6supported = true): string => {
    if (!value) return '';
    const ipType = new RegExp(REGEX_RECOGNIZE_IPV6).test(value) ? IPType.IPV6 : IPType.IPV4;

    if (value.includes('-')) return isValidIPInRange(value, ipType, isIPV6supported);
    else if (value.includes('/')) return isValidSubnetIP(value, ipType, isIPV6supported);
    else return checkSingleIp(value, isIPV6supported);
};

export const checkSingleIp = (value: string, isIPV6supported = true): string => {
    const ipType = new RegExp(REGEX_RECOGNIZE_IPV6).test(value) ? IPType.IPV6 : IPType.IPV4;
    if (isIPV6supported && ipType === IPType.IPV6) return isValidIPv6(value);
    return isValidIPv4(value);
};